<app-main-navbar></app-main-navbar>

<div class="container-fluid mt-4">
  <div class="d-flex justify-content-between align-items-center mb-3">
    <div class="d-flex align-items-center gap-2">
      <button class="btn btn-link p-0 me-2" (click)="goBackToAdminPanel()">
        <i class="bi bi-arrow-left"></i> Back to Admin Panel
      </button>
      <h2 style="font-size: 24px;">Benchmark Portfolios</h2>


    </div>
    <div class="d-flex gap-2">
      <!-- Reorder mode buttons -->
      <ng-container *ngIf="isReorderMode">
        <button class="btn btn-primary" (click)="saveOrder()" [disabled]="isLoading">
          <i class="bi bi-check me-1"></i>Save Order
        </button>
        <button class="btn btn-secondary" (click)="cancelReorder()" [disabled]="isLoading">
          Cancel
        </button>
      </ng-container>
      <!-- Normal mode buttons -->
      <ng-container *ngIf="!isReorderMode">
        <button class="btn btn-outline-secondary" (click)="enterReorderMode()"
          [disabled]="isLoading || benchmarks.length < 2" matTooltip="Drag to reorder benchmarks">
          <i class="bi bi-arrows-move me-2"></i>Reorder
        </button>
        <button class="btn btn-primary" (click)="onCreateBenchmark()" [disabled]="isLoading">
          <i class="bi bi-plus-circle me-2"></i>Create Benchmark
        </button>
      </ng-container>
    </div>
  </div>
  <!-- Information Notice -->
<div class="alert alert-info mb-3" role="alert"
  style="background-color: #e7f3ff; border-left: 4px solid #5e1075; border-radius: 4px;">
  <div class="d-flex align-items-start">
    <strong style="color: #5e1075; margin-right: 8px;">ℹ️ Note for Admin:</strong>
    <div>
      After creating a new benchmark, please click <strong>View Companies</strong> and load the table once. This generates the cache so that users can access the data faster.
      <br><br>
      The cache key includes the <code>date_eom</code>. Whenever the SIA pre-calculated data is updated and <code>date_eom</code> changes, the benchmarks need to be refreshed. This can be done by clicking <strong>View Companies</strong> again for each benchmark. This ensures users receive faster results once the data is cached.
    </div>
  </div>
</div>



  <!-- Create Benchmark Dialog -->
  <div *ngIf="showCreateDialog" class="card mb-4">
    <div class="card-body">
      <h5 class="card-title">Create New Benchmark Portfolio</h5>
      <form [formGroup]="createForm" (ngSubmit)="submitCreate()">
        <div class="mb-3">
          <mat-form-field appearance="outline" class="w-100">
            <mat-label>Benchmark Name</mat-label>
            <input matInput formControlName="benchmark_name" placeholder="e.g., MSCI World" required>
            <mat-error *ngIf="createForm.get('benchmark_name')?.hasError('required')">
              Benchmark name is required
            </mat-error>
          </mat-form-field>
        </div>
        <div class="d-flex gap-2">
          <button type="submit" class="btn btn-primary" [disabled]="createForm.invalid || isLoading">
            Create
          </button>
          <button type="button" class="btn btn-secondary" (click)="cancelCreate()">
            Cancel
          </button>
        </div>
      </form>
    </div>
  </div>

  <!-- Benchmarks Table -->
  <div class="card">
    <div class="card-body">
      <div *ngIf="isLoading" class="text-center p-4">
        <mat-spinner diameter="40"></mat-spinner>
      </div>

      <div *ngIf="!isLoading && benchmarks.length === 0" class="text-center p-4">
        <p class="text-muted">No benchmarks created yet. Create your first benchmark portfolio above.</p>
      </div>

      <!-- Reorder mode table -->
      <div *ngIf="!isLoading && benchmarks.length > 0 && isReorderMode" class="reorder-table">
        <table class="table table-hover mb-0">
          <thead>
            <tr>
              <th style="width: 50px;"></th>
              <th>Benchmark Name</th>
              <th>Description</th>
              <th>Companies</th>
              <th>Created</th>
              <th>Visible</th>
            </tr>
          </thead>
          <tbody cdkDropList (cdkDropListDropped)="onDrop($event)">
            <tr *ngFor="let benchmark of benchmarks" cdkDrag class="drag-row">
              <td class="drag-handle" cdkDragHandle>
                <i class="bi bi-grip-vertical"></i>
              </td>
              <td><strong>{{ benchmark.benchmark_name }}</strong></td>
              <td class="text-muted small">{{ benchmark.description ? (benchmark.description.length > 30 ? benchmark.description.slice(0, 30) + '...' : benchmark.description) : '-' }}</td>
              <td>{{ benchmark.company_count || 0 }}</td>
              <td>{{ formatDate(benchmark.created_at) }}</td>
              <td>
                <i class="bi" [class.bi-eye]="benchmark.is_visible" [class.bi-eye-slash]="!benchmark.is_visible"
                  [class.text-success]="benchmark.is_visible" [class.text-muted]="!benchmark.is_visible"></i>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Normal mode table -->
      <table *ngIf="!isLoading && benchmarks.length > 0 && !isReorderMode" mat-table [dataSource]="benchmarks"
        class="w-100">
        <ng-container matColumnDef="name">
          <th mat-header-cell *matHeaderCellDef>Benchmark Name</th>
          <td mat-cell *matCellDef="let benchmark">
            <!-- Edit mode -->
            <div *ngIf="editingBenchmark?.benchmark_id === benchmark.benchmark_id"
              class="d-flex align-items-center gap-2">
              <mat-form-field appearance="outline" class="edit-field">
                <input matInput [formControl]="$any(editForm.get('benchmark_name'))" placeholder="Benchmark name">
              </mat-form-field>
              <button class="btn btn-sm btn-primary" (click)="submitEdit()" [disabled]="editForm.invalid">
                <i class="bi bi-check"></i>
              </button>
              <button class="btn btn-sm btn-secondary" (click)="cancelEdit()">
                <i class="bi bi-x"></i>
              </button>
            </div>
            <!-- Display mode -->
            <div *ngIf="editingBenchmark?.benchmark_id !== benchmark.benchmark_id"
              class="d-flex align-items-center gap-2">
              <strong>{{ benchmark.benchmark_name }}</strong>
              <button class="btn btn-sm btn-link p-0" (click)="onEditBenchmark(benchmark)" matTooltip="Edit name">
                <i class="bi bi-pencil text-secondary"></i>
              </button>
            </div>
          </td>
        </ng-container>

        <ng-container matColumnDef="description">
          <th mat-header-cell *matHeaderCellDef>Description</th>
          <td mat-cell *matCellDef="let benchmark">
            <!-- Edit mode: inline textarea -->
            <div *ngIf="editingDescriptionId === benchmark.benchmark_id" class="d-flex align-items-start gap-2">
              <textarea [formControl]="$any(descriptionForm.get('description'))"
                class="form-control description-textarea" rows="2" placeholder="Enter description..."></textarea>
              <button class="btn btn-sm btn-primary" (click)="submitDescription()">
                <i class="bi bi-check"></i>
              </button>
              <button class="btn btn-sm btn-secondary" (click)="cancelDescription()">
                <i class="bi bi-x"></i>
              </button>
            </div>
            <!-- Display mode: truncated text with edit icon -->
            <div *ngIf="editingDescriptionId !== benchmark.benchmark_id" class="d-flex align-items-center gap-2">
              <span class="description-text" [class.text-muted]="!benchmark.description">
                {{ benchmark.description ? (benchmark.description.length > 50 ? benchmark.description.slice(0, 50) + '...' : benchmark.description) : 'No description' }}
              </span>
              <button class="btn btn-sm btn-link p-0" (click)="onEditDescription(benchmark)" matTooltip="Edit description">
                <i class="bi bi-pencil text-secondary"></i>
              </button>
            </div>
          </td>
        </ng-container>

        <ng-container matColumnDef="company_count">
          <th mat-header-cell *matHeaderCellDef>Companies</th>
          <td mat-cell *matCellDef="let benchmark">
            {{ benchmark.company_count || 0 }}
          </td>
        </ng-container>

        <ng-container matColumnDef="created_at">
          <th mat-header-cell *matHeaderCellDef>Created</th>
          <td mat-cell *matCellDef="let benchmark">
            {{ formatDate(benchmark.created_at) }}
          </td>
        </ng-container>

        <ng-container matColumnDef="visibility">
          <th mat-header-cell *matHeaderCellDef>Visible</th>
          <td mat-cell *matCellDef="let benchmark">
            <button class="btn btn-sm" [class.btn-success]="benchmark.is_visible"
              [class.btn-outline-secondary]="!benchmark.is_visible" (click)="onToggleVisibility(benchmark)"
              [matTooltip]="benchmark.is_visible ? 'Click to hide from users' : 'Click to show to users'">
              <i class="bi" [class.bi-eye]="benchmark.is_visible" [class.bi-eye-slash]="!benchmark.is_visible"></i>
            </button>
          </td>
        </ng-container>

        <ng-container matColumnDef="actions">
          <th mat-header-cell *matHeaderCellDef>Actions</th>
          <td mat-cell *matCellDef="let benchmark">
            <button class="btn btn-sm btn-outline-secondary me-2" (click)="onViewCompanies(benchmark)"
              [disabled]="isLoading">
              <i class="bi bi-list-ul me-1"></i>View Companies
            </button>
            <button class="btn btn-sm btn-primary me-2" (click)="onUploadCompanies(benchmark)" [disabled]="isLoading">
              <i class="bi bi-upload me-1"></i>Upload Companies
            </button>
            <button class="btn btn-sm btn-danger" (click)="onDeleteBenchmark(benchmark)" [disabled]="isLoading"
              matTooltip="Delete Benchmark">
              <i class="bi bi-trash me-1"></i>Delete
            </button>
          </td>
        </ng-container>

        <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
        <tr mat-row *matRowDef="let row; columns: displayedColumns;"></tr>
      </table>
    </div>
  </div>

  <!-- Selected Benchmark Companies -->
  <div class="card mt-3" *ngIf="selectedBenchmark">
    <div class="card-body">
      <div class="d-flex justify-content-between align-items-center mb-3">
        <h5 class="mb-0">Companies in {{ selectedBenchmark.benchmark_name }}</h5>
        <input type="text" class="form-control form-control-sm" style="max-width: 260px;"
          placeholder="Search by name, Entity ID, ISIN" [(ngModel)]="companySearch"
          (ngModelChange)="applyCompanyFilter()">
      </div>

      <div *ngIf="filteredCompanies.length === 0 && !loadingCompanies" class="text-muted small">
        No companies found.
      </div>
      <!-- add spinner if loadingCompanies is true -->
      <div *ngIf="loadingCompanies" class="text-center p-4">
        <mat-spinner diameter="40"></mat-spinner>
      </div>

      <div class="table-responsive" *ngIf="filteredCompanies.length > 0">
        <table class="table table-sm table-hover mb-0">
          <thead>
            <tr>
              <th style="width: 35%;">Name</th>
              <th style="width: 20%;">Entity ID</th>
              <th style="width: 20%;">ISIN</th>
              <th style="width: 15%;">Added</th>
              <th style="width: 10%;"></th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let c of filteredCompanies">
              <td>{{ c.entity_name }}</td>
              <td>{{ c.entityid }}</td>
              <td>{{ c.isin }}</td>
              <td>{{ c.added_at | date:'yyyy-MM-dd' }}</td>
              <td class="text-end">
                <button class="btn btn-sm btn-outline-danger" (click)="onDeleteCompany(c)" matTooltip="Delete Company">
                  <i class="bi bi-trash"></i>
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</div>